---
title: "The 7 best rank tracking APIs in 2026, priced per keyword check"
description: "I signed up for seven rank tracking APIs, sent the same Google searches through each, and priced 30,000 keyword checks a month. Here's which one to pick."
date: "2026-09-01"
postType: "alternatives"
author: "Kevin Wang"
tags: ["seo", "rank-tracking", "serp", "api", "comparison"]
keyword: "rank tracking api"
ogImage: "/og/rank-tracking-api.png"
---

export const medianLatency = [
  { label: "Serper", value: 0.8 },
  { label: "Scrapingdog", value: 0.8 },
  { label: "SearchApi.io", value: 1.5 },
  { label: "AnyAPI", value: 2.2 },
  { label: "Bright Data", value: 2.3 },
];

<Callout type="tip" title="Key findings">

- **Under about 7,000 keywords a month, use [Bright Data](https://brightdata.com/products/serp-api/rank-tracking).** The first 5,000 requests every month are free.
- **Above that, [AnyAPI](https://getanyapi.com/api/google) is the cheapest option here, and it answers in real time.**
- **[DataForSEO](https://dataforseo.com/apis/serp-api/pricing)'s queue is the next cheapest, and the one to pick if an overnight job needs its device, location, and depth controls and can wait 45 minutes.**
- **Three of the seven headline prices aren't the price of a keyword check.** I explain each one below.

</Callout>

Every rank tracker does the same job underneath. Run a Google search for a keyword, find your URL in the results, write down the position, do it again tomorrow. The code is easy. The hard part is that Google doesn't want you doing it, so you rent someone else's access, and the pricing pages for that access are some of the worst I've read.

I spent a week signing up for every option here, reading their pricing pages with a screenshot tool instead of my eyes, and sending the same five searches through every API that would give me a key. Where one wins, I say so by name.

The short version: the cheapest option depends on how many keywords you check a month, and the price on the pricing page is often not the price you'll pay.

## The best rank tracking APIs

- [Bright Data](#best-rank-tracking-api-for-small-keyword-lists) for small keyword lists
- [AnyAPI](#best-rank-tracking-api-for-pay-per-request-checks-at-volume) for pay-per-request checks at volume
- [DataForSEO](#best-rank-tracking-api-for-queued-checks-with-device-location-and-depth-controls) for queued checks with device, location, and depth controls
- [SearchApi.io](#best-rank-tracking-api-for-top-100-positions-in-one-call) for top-100 positions in one call
- [Scrapingdog](#best-rank-tracking-api-for-fast-responses-on-a-monthly-plan) for fast responses on a monthly plan
- [Serper](#best-rank-tracking-api-for-a-free-tier-you-can-actually-build-on) for a free tier you can actually build on
- [SerpApi](#best-rank-tracking-api-for-multi-engine-coverage-and-documentation) for multi-engine coverage and documentation

## What makes a good rank tracking API?

I'm assuming you're building the tracker, not buying a dashboard. You have a list of keywords, a cron job, and a database, and you need a data source that won't surprise you. Here's what I compared them on.

- **Structured positions, not HTML.** If the API hands you a page of Google's HTML, you're now maintaining a Google parser. That is the job you were trying to outsource.
- **A price you can compute before you run the job.** One keyword check is one Google search returning the first page of results. Every price below is normalized to that, at a workload of 1,000 keywords checked once a day, which is 30,000 checks a month.
- **Delivery that matches the job.** A nightly batch doesn't care whether a result takes two seconds or forty minutes. A dashboard that refreshes when a customer clicks does.
- **A free tier big enough to test with.** Forty searches tells you the auth works. It doesn't tell you what the response looks like across your keyword list.

<Callout type="note" title="How this was tested, and its limits">

I did not grade rank accuracy, uptime, or search engines other than Google. The speed numbers come from five identical US English searches per provider, sent one after another from my laptop, so they're a smell test of the response shape and the rough speed, not a benchmark.

I couldn't get keys for DataForSEO or SerpApi without a card or a business email, so their numbers are from their pricing pages only.

Bright Data's HTML-instead-of-JSON result may be specific to the trial zone they created for me.

Every price is one first-page search per keyword, one location, desktop, no retries. Mobile, multiple locations, and deep pages each multiply the bill.

Prices checked on 2026-09-01. Enterprise deals aren't included.

</Callout>

## The best rank tracking APIs at a glance

| Tool | Best for | Why pick it | Pricing at 30,000 checks a month |
| --- | --- | --- | --- |
| Bright Data | Small keyword lists | 5,000 free requests every month | $37.50 ($1.50 per 1,000 after the free 5,000) |
| AnyAPI | Pay-per-request checks at volume | No plan, no expiry, price shown before you call | <Price sku="google.search" calls={30000} /> (<Price sku="google.search" /> per 1,000) |
| DataForSEO | Queued checks with device, location, and depth controls | Queued mode at $0.60 per 1,000 | $18 queued, $60 real time |
| SearchApi.io | Top-100 positions in one call | Dedicated rank tracking engine | $100 ($3.33 per 1,000 on the plan you'd need) |
| Scrapingdog | Fast responses on a monthly plan | Fastest median in my test | $40 ($1.00 per 1,000 of Google searches) |
| Serper | A free tier you can build on | 2,500 free searches | $50 top-up covers 50,000 searches, expires in 6 months |
| SerpApi | Multi-engine coverage and docs | Failed and cached searches don't count | $275 ($9.17 per 1,000) |

<Chart caption="Median response time for five identical Google searches per provider, measured 2026-09-01 from one machine, in seconds. DataForSEO and SerpApi aren't plotted because I couldn't get a trial key for either without a business email or a card.">
  <BarChart data={medianLatency} showValues baseline={0} height={220} format={(n) => `${n.toFixed(1)} s`} ariaLabel="Median response time in seconds: Serper 0.8, Scrapingdog 0.8, SearchApi.io 1.5, AnyAPI 2.2, Bright Data 2.3." />
</Chart>

## Best rank tracking API for small keyword lists

### Bright Data

<Figure src="/og/screenshots/rank-tracking-api-bright-data-rank-tracking-price.png" alt="Bright Data's rank tracking page showing the pay-as-you-go price and no-commitment plan" width={1280} height={900} />

**Bright Data pros:**

- 5,000 free requests every month, forever, with no plan to sign up for
- You only pay for requests that succeed
- City-level location targeting, which most of the others don't offer

**Bright Data cons:**

- Slowest in my test, and the trial zone I was given returned Google's HTML instead of parsed positions on every call
- The pricing page hides its own number from copy and paste

If your keyword list is small, this is the answer and you can stop reading. Five thousand free requests a month covers 160 keywords checked daily, and there's no plan and no expiry to think about. Past the free 5,000 you pay $1.50 per 1,000 with no commitment, which is still a fair price.

Two things bothered me. Bright Data's docs say to pass `format: json` for structured results, and I did, and all five of my searches came back as a page of Google HTML wrapped in a JSON envelope. That may be a quirk of the trial zone they set up for me, so check what yours returns before you write the parser. And the pricing page renders "$1.50" with invisible characters between the digits, so a text scrape of the page reads "$1". I only caught it because I compared a screenshot against my notes. Read the number off the screen, not out of the DOM.

**Bright Data pricing:** 5,000 requests a month free, then $1.50 per 1,000 with no commitment. Thirty thousand checks a month is $37.50.

## Best rank tracking API for pay-per-request checks at volume

### AnyAPI

<Figure src="/og/screenshots/rank-tracking-api-anyapi-google-search.png" alt="The reference table on AnyAPI's google.search page: the last request fields, the normalized result fields title, link, snippet, and position, and the price, $0.0004 a request or $0.40 per 1,000" width={1280} height={900} />

**AnyAPI pros:**

- <Price sku="google.search" /> per 1,000 searches with no plan, no minimum, and nothing that expires
- Every result comes back as the same clean JSON: title, link, snippet, position
- A separate top-100 endpoint returns absolute positions 1 through 100 in one call

**AnyAPI cons:**

- Slower than Serper and Scrapingdog in my run, and one of my five calls took almost four seconds
- The free credit is about <Price sku="google.search" budget={0.1} /> searches, enough to see the shape and not much more

The pitch is that you put money in a wallet and a Google search takes <Price sku="google.search" per="call" /> out of it. There's no plan to size, no credits that expire, and the price is printed on the endpoint page before you call it. At 30,000 checks a month the bill is <Price sku="google.search" calls={30000} />, the lowest on this page, DataForSEO's queue included.

The line where it beats Bright Data is about 7,000 checks a month. Below that, Bright Data's free 5,000 does more work than AnyAPI's lower rate. Above it, the rate wins. If you're at 1,000 keywords a day you're well past the line.

The honest weakness is speed. Our median in this run was 2.2 seconds against Serper's 0.8, and one call took 3.7. For a nightly job that's nothing. For a "check my rank now" button I'd want that faster, and I'm working on it. The other thing to know is that `google.search` returns the first page, so a deep position walk means paging with the cursor. If you need the full top 100 for a keyword, [`google.search_100`](https://getanyapi.com/api/google) does it in one request for <Price sku="google.search_100" /> per 1,000, against the $1.50 Bright Data charges for a first page.

**AnyAPI pricing:** <Price sku="google.search" /> per 1,000 Google searches, pay as you go from a prepaid wallet, rising to <Price sku="google.search" failover /> per 1,000 for searches the cheapest source can't answer and a dearer one does. New accounts get $0.10 of credit, which is about <Price sku="google.search" budget={0.1} /> searches. Thirty thousand checks a month is <Price sku="google.search" calls={30000} /> when the cheapest source answers.

## Best rank tracking API for queued checks with device, location, and depth controls

### DataForSEO

<Figure src="/og/screenshots/rank-tracking-api-dataforseo-serp-modes.png" alt="DataForSEO's SERP pricing page showing queued and live retrieval modes" width={1280} height={900} />

**DataForSEO pros:**

- Standard Queue costs $0.60 per 1,000, the cheapest paid rate on this page after AnyAPI's <Price sku="google.search" />
- A free sandbox you can code against before you pay anything
- The most knobs of any option: device, location, depth, operators

**DataForSEO cons:**

- The cheap price is a queue with a 45-minute target turnaround, and the real-time mode costs $2.00 per 1,000
- A `site:` operator multiplies the price by 5, and rank trackers use `site:` constantly
- $50 minimum payment to start

DataForSEO sells the same search at three speeds and they are three different products. Standard Queue is $0.60 per 1,000 and the page heading says results come back in about five minutes. The asterisk under it says the target is 45 minutes. Priority Queue is $1.20 with a turnaround of up to a minute. Live Mode is $2.00 and answers in a few seconds. So the headline price only applies if your job can wait most of an hour. Even then, $18 a month for 30,000 checks is more than AnyAPI's <Price sku="google.search" calls={30000} /> in real time, so the reason to wait for DataForSEO is its controls: device, location, and depth.

Watch the modifiers. Every search is billed as one page of ten results, deeper pages bill again, and thirteen search operators including `site:` multiply the price by five. A rank tracker that runs `site:yourdomain.com keyword` on the Standard Queue is paying $3.00 per 1,000, not $0.60.

I didn't get to measure it. DataForSEO wants a $50 minimum payment before you can make a live call, and the sandbox returns canned data.

**DataForSEO pricing:** $0.60 per 1,000 queued, $1.20 priority, $2.00 live, with a $50 minimum payment. Thirty thousand checks a month is $18 queued or $60 live.

## Best rank tracking API for top-100 positions in one call

### SearchApi.io

<Figure src="/og/screenshots/rank-tracking-api-searchapi-rank-tracking-contract.png" alt="SearchApi.io's rank tracking documentation showing the dedicated engine and deeper position controls" width={1280} height={900} />

**SearchApi.io pros:**

- A dedicated rank tracking engine that returns up to 100 organic positions in one request, with duplicates removed
- Solid in my test: every call came back structured, 1.5 seconds median
- Clean docs with a request builder

**SearchApi.io cons:**

- Monthly plans only, starting at $4 per 1,000 searches
- 100 free searches, which is one afternoon of testing

If you need to know that a page sits at position 63, most of these APIs make you fetch pages one through seven and stitch them. SearchApi.io has a separate engine for exactly this, and it is the reason to pick it. One request, one hundred positions, no stitching.

You pay for that convenience in the plan structure. The $40 plan covers 10,000 searches, so 30,000 checks a month puts you on the $100 plan at $3.33 per 1,000. If you only ever look at page one, that's a premium for depth you aren't reading.

**SearchApi.io pricing:** $40 a month for 10,000 searches, $100 a month for 30,000. Larger plans get down to $1 per 1,000.

## Best rank tracking API for fast responses on a monthly plan

### Scrapingdog

<Figure src="/og/screenshots/rank-tracking-api-scrapingdog-google-serp-plan.png" alt="Scrapingdog's monthly pricing table showing plan credits and the Google SERP billing row" width={1280} height={900} />

**Scrapingdog pros:**

- Tied for fastest median in my test at 0.8 seconds
- Simplest request of the bunch: one GET with your key, the query, a country, and a page number
- Returns organic results, SERP features, and pagination in one response

**Scrapingdog cons:**

- A Google search costs 5 credits, so every number on the pricing page is 5x smaller than it looks
- One of my five searches came back as a 403, in 117 milliseconds

The plans are sold in "request credits", and I read that as requests. It isn't. I made seven Google searches on a free account and the dashboard showed 35 credits used, so a Google search is 5 credits. That makes the $40 Lite plan 40,000 searches, not 200,000, and $1.00 per 1,000 instead of the $0.20 the page implies. The free tier is 40 searches, not 200. None of this is written down anywhere I could find. It's still a fair price once you do the division, but do the division.

The one failure in my run was "data enrichment services", which came back 403 in a tenth of a second while the other four queries sailed through. I don't know why. If your tracker retries on 4xx you'll be fine, and I'd want to know whether that call spent credits.

**Scrapingdog pricing:** $40 a month for 200,000 credits, which is 40,000 Google searches. Thirty thousand checks a month fits in the Lite plan.

## Best rank tracking API for a free tier you can actually build on

### Serper

<Figure src="/og/screenshots/rank-tracking-api-serper-starter-topup.png" alt="Serper's Starter top-up showing the purchase amount, number of queries included, and credit validity" width={1280} height={900} />

**Serper pros:**

- 2,500 free searches, the biggest free tier here after Bright Data's recurring one
- The fastest single response in my whole test, and the fastest median
- Your remaining balance comes back in every response, so a cron job can stop before it runs dry

**Serper cons:**

- Paid access is a $50 top-up for 50,000 searches that expires after six months
- No monthly option and no smaller top-up

Serper is what I'd hand a developer who wants to see a working tracker by lunch. Two and a half thousand free searches is enough to run a real keyword list for a couple of days. The request is a POST with the query, country, and language, and the response has organic results, "people also ask", and your balance.

The paid model is the catch. You buy $50 of searches and they're gone in six months whether you used them or not. At 1,000 keywords a day the block lasts about 50 days, so the expiry never bites. At 100 keywords a day it lasts 500 days, and you'll have paid for a lot of searches you never sent.

**Serper pricing:** 2,500 searches free, then $50 for 50,000 searches, valid for six months. That's $1.00 per 1,000 if you use them all.

## Best rank tracking API for multi-engine coverage and documentation

### SerpApi

<Figure src="/og/screenshots/rank-tracking-api-serpapi-monthly-plans.png" alt="SerpApi's monthly pricing page showing how many searches the free, Starter, and Big Data plans include" width={1280} height={900} />

**SerpApi pros:**

- The most search engines and the most thorough response docs of any option here
- Cached, errored, and failed searches don't count against your plan
- Real support, which none of the cheaper options really offer

**SerpApi cons:**

- $25 a month buys 1,000 searches, and 30,000 a month means the $275 plan
- 250 free searches a month

SerpApi is the incumbent, and the docs show it. Every field of every engine is documented, the playground is good, and if a search fails you don't pay for it. If you're tracking Bing and YouTube alongside Google, or you want a vendor with a support inbox that answers, this is the mature choice.

For a Google-only tracker the price is hard to defend. Thirty thousand checks a month is the $275 Big Data plan, which is more than twenty times the AnyAPI bill and seven times Bright Data's for the same searches. I couldn't measure it without a card, so I can't tell you it's slow or fast, only that it's expensive.

**SerpApi pricing:** 250 searches a month free, $25 a month for 1,000, $275 a month for 30,000.

## Which rank tracking API should you use?

- Under about 7,000 checks a month: Bright Data, and verify the response is JSON before you build on it.
- Over about 7,000 checks a month, overnight or in real time: AnyAPI.
- A nightly batch that can wait an hour and needs device, location, or depth controls: DataForSEO's Standard Queue.
- You need positions past page one in a single call: SearchApi.io, or AnyAPI's top-100 endpoint at <Price sku="google.search_100" /> per 1,000.
- You want a working prototype today for free: Serper.
- You're on a monthly plan anyway and want the fastest response: Scrapingdog.
- You track more engines than Google and want support: SerpApi.

If you're comparing scraping platforms more broadly, the [Apify alternatives comparison](/blog/apify-alternatives) covers that. The [Google Search API page](/api/google) has the live request format and the current price.

<CTA to="/api/google" note={<>Google results as clean JSON, <Price sku="google.search" /> per 1,000 searches, no plan and nothing that expires. New accounts get free credit, no card required.</>}>
  Try the Google Search API
</CTA>

Use data responsibly and follow AnyAPI's [Acceptable Use Policy](/acceptable-use).
